<script setup>
import { useI18n } from 'vue-i18n'
import { defineAsyncComponent } from 'vue'
import { useCountry } from '@/mixins/useCountry'

const FooterLinkFamily = defineAsyncComponent(() => import('./FooterLinkFamily.vue'))
const FooterSubscriptionForm = defineAsyncComponent(() => import('./FooterSubscriptionForm.vue'))
const country = useCountry()

const { t, locale } = useI18n()

const linkTree = [
  {
    title: t('shopping'),
    items: [
      { label: t('delivery_payment_opts'), linkTo: { name: 'deliveryAndPaymentPath' } },
      { label: t('complaint'), linkTo: { name: 'complaintsPath' } },
      { label: t('loyality_program'), linkTo: { name: 'mixitAfiliatePath' } },
      { label: t('terms_and_conditions'), linkTo: { name: 'tradeTermsPath' } },
      { label: t('privacy_policy'), linkTo: { name: 'privacyTermsPath' } },
      { label: t('mixit_plus_terms'), linkTo: { name: 'mixitPlusTermsPath' }, localesExcept: ['at', 'fr'] },
      { label: t('impressum'), linkTo: { name: 'impressumPath' }, localesOnly: ['de', 'at'] },
      { label: t('whistleblowing'), linkTo: { name: 'whistleblowingPath' } }
    ]
  },
  {
    title: t('services'),
    items: [
      { label: t('mixit-club'), linkTo: { name: 'clubPath' }, localesOnly: ['cs', 'sk'] },
      { label: t('mixit-plus'), linkTo: { name: 'mixitPlusPath' } }
    ],
  },
  {
    title: t('for_companies'),
    items: [
      { label: t('company_gifts'), linkTo: { name: 'companyGiftsPath' } },
      { label: t('wholesale'), href: t('wholesale_link'), localesExcept: ['de', 'at'] }
    ],
  },
  {
    title: t('mixit'),
    items: [
      { label: t('about_us'), linkTo: { name: 'aboutUsPath' } },
      { label: t('faq'), linkTo: { name: 'faqPath' } },
      { label: t('contact'), linkTo: { name: 'contactPath' } },
      { label: t('distributors'), linkTo: { name: 'distributorsPath' }, localesExcept: ['ro', 'hu', 'de'] },
      { label: t('job'), linkTo: { name: 'jobOfferPath' }, localesOnly: ['cs'] },
      { label: t('blog'), linkTo: { name: 'blogPath' } }
    ],
  }
];
</script>

<template>
  <div class="row px-0 mx-0 items-start sm:mt-6 mt-4 pt-3 justify-between">
    <FooterLinkFamily
      class="col-12 sm:col-6 lg:col-auto pl-0"
      :link-family="linkTree[0]"
    />
    <div class="col-6 lg:col-auto pl-0 mt-5 sm:mt-0">
      <FooterLinkFamily
        v-if="country !== 'at' && country !== 'fr'"
        class="mb-5"
        :link-family="linkTree[1]"
      />
      <FooterLinkFamily :link-family="linkTree[2]" />
    </div>
    <FooterLinkFamily
      v-if="locale !== 'de' || locale !== 'ro'"
      class="col-6 sm:col-4 lg:col-auto pl-0 mt-5 lg:mt-0"
      :link-family="linkTree[3]"
    />
    <div class="col-12 sm:col-8 lg:col-4 px-0 mt-5 lg:mt-0">
      <FooterSubscriptionForm />
    </div>
  </div>
</template>

<i18n>
  {
    "cs": {
    "about_us": "O nás",
    "blog": "Blog",
    "company_gifts": "Firemní dárky",
    "complaint": "Reklamace",
    "contact": "Kontakt",
    "delivery_payment_opts": "Dopravy a platby",
    "distributors": "Kde jsme",
    "faq": "Otázky (FAQ)",
    "for_companies": "Pro firmy",
    "impressum": "Impressum",
    "job": "Práce / brigáda",
    "loyality_program": "Věrnostní program",
    "mixit": "Mixit",
    "mixit-club": "Mixit Klub",
    "mixit-plus": "Mixit plus+",
    "mixit_plus_terms": "Obchodní podmínky Mixit plus+",
    "privacy_policy": "Zásady ochrany osobních údajů",
    "services": "Služby",
    "shopping": "Nakupování",
    "terms_and_conditions": "Obchodní podmínky",
    "voucher_permalink": "mixit-plus-na-12-mesicu",
    "whistleblowing": "Ochrana oznamovatelů",
    "wholesale": "Velkoobchod",
    "wholesale_link": "https://velkoobchod.mixit.cz/"
},
    "sk": {
    "about_us": "O nás",
    "blog": "Blog",
    "company_gifts": "Firemné darčeky",
    "complaint": "Reklamácia",
    "contact": "Kontakt",
    "delivery_payment_opts": "Dopravy a platby",
    "distributors": "Kde sme",
    "faq": "Otázky (FAQ)",
    "for_companies": "Pre firmy",
    "impressum": "Impressum",
    "job": "Kariéra",
    "loyality_program": "Vernostný program",
    "mixit": "Mixit",
    "mixit-club": "Mixit Klub",
    "mixit-plus": "Mixit plus+",
    "mixit_plus_terms": "Obchodné podmienky Mixit plus+",
    "privacy_policy": "Zásady ochrany osobných údajov",
    "services": "Výhodné programy",
    "shopping": "Nakupovanie",
    "terms_and_conditions": "Obchodné podmienky",
    "voucher_permalink": "mixit-plus-na-12-mesiacov",
    "whistleblowing": "Ochrana oznamovateľov",
    "wholesale": "Veľkoobchod",
    "wholesale_link": "https://velkoobchod.mixit.sk/"
},
    "pl": {
    "about_us": "O nas",
    "blog": "Blog",
    "company_gifts": "Prezenty firmowe",
    "complaint": "Reklamacje",
    "contact": "Kontakt",
    "delivery_payment_opts": "Dostawa i płatność",
    "distributors": "Gdzie nas znajdziesz?",
    "faq": "Pytania (FAQ)",
    "for_companies": "Dla firm",
    "impressum": "Impressum",
    "job": "Praca / praca dorywcza",
    "loyality_program": "Program lojalnościowy",
    "mixit": "Mixit",
    "mixit-club": "Klub Mixit",
    "mixit-plus": "Mixit plus+",
    "mixit_plus_terms": "Regulamin Mixit plus+",
    "privacy_policy": "Polityka prywatności",
    "services": "Usługi",
    "shopping": "Zakupy",
    "terms_and_conditions": "Warunki handlowe",
    "voucher_permalink": "mixit-plus-na-12-miesiecy",
    "whistleblowing": "Ochrona sygnalistów",
    "wholesale": "Sprzedaż hurtowa",
    "wholesale_link": "https://hurtownia.mixit.pl/"
},
    "hu": {
    "about_us": "Rólunk",
    "blog": "Blog",
    "company_gifts": "Céges ajándékok",
    "complaint": "Panaszok",
    "contact": "Kapcsolat",
    "delivery_payment_opts": "Szállítás és fizetés",
    "distributors": "Hol vagyunk",
    "faq": "Kérdések (GYIK)",
    "for_companies": "Cégeknek",
    "impressum": "Impresszum",
    "job": "Karrier",
    "loyality_program": "Hűségprogram",
    "mixit": "Mixit",
    "mixit-club": "Mixit Klub",
    "mixit-plus": "Mix Plus+",
    "mixit_plus_terms": "Általános Szerződési Feltételek Mixit plus+",
    "privacy_policy": "Adatvédelmi irányelvek",
    "services": "Szolgáltatások",
    "shopping": "Vásárlás",
    "terms_and_conditions": "Általános szerződési feltételek",
    "voucher_permalink": "mixit-plusz-12-honapra",
    "whistleblowing": "Bejelentők védelme",
    "wholesale": "Nagykereskedelem",
    "wholesale_link": "https://b2b.mix.it/"
},
    "de": {
    "about_us": "Über uns",
    "blog": "Blog",
    "company_gifts": "Firmengeschenke",
    "complaint": "Reklamation",
    "contact": "Kontakt",
    "delivery_payment_opts": "Versand und Bezahlung",
    "distributors": "Wo sind wir?",
    "faq": "Fragen (FAQ)",
    "for_companies": "Für Unternehmen",
    "impressum": "Impressum",
    "job": "Karriere",
    "loyality_program": "Treueprogramm",
    "mixit": "Mixit",
    "mixit-club": "Mixit Club",
    "mixit-plus": "Mixit plus+",
    "mixit_plus_terms": "Allgemeine Geschäftsbedingungen Mixit plus+",
    "privacy_policy": "Datenschutzbestimmungen",
    "services": "Vorteilsprogramme",
    "shopping": "Einkaufen",
    "terms_and_conditions": "Allgemeine Geschäftsbedingungen",
    "voucher_permalink": "mixit-plus-fuer-12-monate",
    "whistleblowing": "Hinweisgeberschutz",
    "wholesale": "Großhandel",
    "wholesale_link": "https://grosshandel.mixit.de/"
},
    "at": {
    "about_us": "Über uns",
    "blog": "Blog",
    "company_gifts": "Firmengeschenke",
    "complaint": "Reklamation",
    "contact": "Kontakt",
    "delivery_payment_opts": "Versand und Bezahlung",
    "distributors": "Wo sind wir?",
    "faq": "Fragen (FAQ)",
    "for_companies": "Für Unternehmen",
    "impressum": "Impressum",
    "job": "Karriere",
    "loyality_program": "Treueprogramm",
    "mixit": "Mixit",
    "mixit-club": "Mixit Club",
    "mixit-plus": "Mixit plus+",
    "mixit_plus_terms": "Allgemeine Geschäftsbedingungen Mixit plus+",
    "privacy_policy": "Datenschutzbestimmungen",
    "services": "Vorteilsprogramme",
    "shopping": "Einkaufen",
    "terms_and_conditions": "Allgemeine Geschäftsbedingungen",
    "voucher_permalink": "mixit-plus-fuer-12-monate",
    "whistleblowing": "Hinweisgeberschutz",
    "wholesale": "Großhandel",
    "wholesale_link": "https://grosshandel.mixit.de/"
},
    "ro": {
    "about_us": "Despre noi",
    "blog": "Blog",
    "company_gifts": "Cadouri corporative",
    "complaint": "Reclamații",
    "contact": "Contact",
    "delivery_payment_opts": "Transport și plată",
    "distributors": "",
    "faq": "Întrebări (FAQ)",
    "for_companies": "Pentru firme",
    "impressum": "Drepturile de autor",
    "job": "Carieră",
    "loyality_program": "Program de loialitate",
    "mixit": "Mixit",
    "mixit-club": "Clubul Mixit",
    "mixit-plus": "Mixit plus+",
    "mixit_plus_terms": "Termeni și condiții Mixit plus+",
    "privacy_policy": "Politica de confidențialitate a datelor personale",
    "services": "Programe avantajoase",
    "shopping": "Cumpărături",
    "terms_and_conditions": "Termeni și condiții",
    "voucher_permalink": "mixit-plus-de-12-luni",
    "whistleblowing": "Protecția avertizorilor de integritate",
    "wholesale": "Angro",
    "wholesale_link": ""
},
    "fr": {
    "about_us": "À propos de nous",
    "blog": "Blog",
    "company_gifts": "Cadeaux d'entreprise",
    "complaint": "Réclamation",
    "contact": "Contact",
    "delivery_payment_opts": "Livraison et paiements",
    "distributors": "Où sommes-nous",
    "faq": "Questions (FAQ)",
    "for_companies": "Pour les entreprises",
    "impressum": "Empreinte",
    "job": "Travail /emploi saisonnier",
    "loyality_program": "Programme de fidélité",
    "mixit": "Mixit",
    "mixit-club": "Mixit Club",
    "mixit-plus": "Mixit plus+",
    "mixit_plus_terms": "Conditions générales de Mixit plus+",
    "privacy_policy": "Politique de confidentialité",
    "services": "Offres avantageuses",
    "shopping": "Achats",
    "terms_and_conditions": "Conditions générales de vente",
    "voucher_permalink": "mixit-plus-pour-12-mois",
    "whistleblowing": "Protection des lanceurs d’alerte",
    "wholesale": "Vente en gros",
    "wholesale_link": "https://velkoobchod.mixit.cz/"
}
  }
</i18n>
